import { useSyncExternalStore } from "react";
import {
  getResolvedFlags,
  getResolvedFlagsSnapshot,
  subscribeFlags,
  setFlagOverride as setFlagOverrideInternal,
  clearFlagOverride as clearFlagOverrideInternal,
  getFlagOverrides as getFlagOverridesInternal,
} from "./flagsClient";

export interface FlagDefinition {
  /** Value returned when no override or remote config value is present. */
  default: boolean;
  /** Description shown in the dev flag UI. */
  description: string;
  /** Short human label for the dev toggle. */
  label: string;
}

/**
 * Registry of all known feature flags. Adding a flag here is the only place
 * you need to touch to get it typed, defaulted, and surfaced in the dev UI.
 *
 * The paid features (AI feng shui, product URL import) used to gate on
 * flags, but they now gate on the Premium entitlement (see `./premium.ts`)
 * so anyone can find the buttons and the upsell overlay handles the "you
 * need to pay" case. Non-paid experimental flags live here.
 *
 * The signature is `Record<string, FlagDefinition>` (rather than a strict
 * `as const` union) so the registry is allowed to be empty without
 * collapsing `FlagKey` to `never` — which would in turn break every
 * helper signature at compile time.
 */
export const FLAGS: Readonly<Record<string, FlagDefinition>> = {
  /**
   * Kill switch for the collaboration-matched first run that paid-social
   * visitors land in (see `./landingFlow.ts`). Default ON; flip the
   * Remote Config boolean to `false` to send ad traffic back to the
   * standard onboarding without a deploy. QA: `?flags=collab_onboarding:0`.
   */
  collab_onboarding: {
    default: true,
    label: "Collab onboarding",
    description:
      "Route paid-social landings (utm_medium=paid_social or ?flow=collab) into the 'design together' first run.",
  },
  /**
   * Kill switch for the feng shui first run that AI ads land in (see
   * `./landingFlow.ts`). Default ON; when off, those visitors fall back
   * to the collab first run. QA: `?flags=feng_shui_onboarding:0`.
   */
  feng_shui_onboarding: {
    default: true,
    label: "Feng shui onboarding",
    description:
      "Route AI ad landings (utm_content ai_* / feng_shui / coffin, or ?flow=fengshui) into the feng shui first run.",
  },
  /**
   * Former kill switch for running real AI as a Firebase anonymous session.
   * Default OFF. Metered AI requires a registered account either way — the
   * server rejects anonymous callers — so turning this on does not grant
   * credits. QA: `?flags=anonymous_trial:1` no longer bypasses sign-in.
   */
  anonymous_trial: {
    default: false,
    label: "Anonymous AI trial",
    description: "Unused for AI access. Metered calls require a registered account.",
  },
  /**
   * Show "Continue with Apple" on the save / sign-in prompts. Default OFF
   * until the Apple provider is configured in the Firebase console.
   */
  apple_sign_in: {
    default: false,
    label: "Sign in with Apple",
    description: "Offer Apple alongside Google (requires the Apple provider in Firebase Auth).",
  },
  /**
   * Show "Email me a code" on the save / sign-in prompts. Default OFF
   * until `EMAIL_OTP_SMTP_URL` holds a real SMTP URL.
   */
  email_otp: {
    default: false,
    label: "Email code sign-in",
    description: "Offer a six-digit email code (works inside in-app browsers). Requires EMAIL_OTP_SMTP_URL.",
  },
  /**
   * Reverse trial: every new account gets Premium for 7 days, no card
   * (`startReverseTrial` callable, `users/{uid}/private/trial`). Default ON.
   */
  reverse_trial: {
    default: true,
    label: "7-day reverse trial",
    description: "Start a week of Premium on sign-up, then fall back to free unless they subscribe.",
  },
  /**
   * Kill switch for the "3D Render" walk-through (see
   * `./render3dRequests.ts`). Default ON; when off the button is hidden
   * everywhere. QA: `?flags=render_3d:0`.
   */
  render_3d: {
    default: true,
    label: "3D Render",
    description: "Show the 3D Render button that opens a life-like walk-through of the current room.",
  },
  /**
   * Kill switch for the 3D walk-through first run that 3D ads land in (see
   * `./landingFlow.ts`). Default ON; when off, those visitors fall back to
   * the collab first run. QA: `?flags=render_3d_onboarding:0`.
   */
  render_3d_onboarding: {
    default: true,
    label: "3D onboarding",
    description:
      "Route 3D ad landings (utm_content r3d_* / render3d / walkthrough, or ?flow=render3d) into the 3D walk-through first run.",
  },
  /**
   * Kill switch for room finishes (see `./finishesRequests.ts`): the
   * Finishes sheet and its doors (3D button + surface taps, Walls chip,
   * Room details row) and the 2D echoes of chosen finishes. Saved finishes
   * still render in 3D when off. Default ON. QA: `?flags=room_finishes:0`.
   */
  room_finishes: {
    default: true,
    label: "Room finishes",
    description: "Let people choose floor, wall and ceiling finishes (and Looks) and see them in 3D.",
  },
  /**
   * Indoor / Outdoor rooms: open sky, ground, boundaries (fences, hedges,
   * a neighbor's house), gates and pergolas. Saved outdoor rooms still
   * render outdoors when off. Default ON. QA: `?flags=outdoor_rooms:0`.
   */
  outdoor_rooms: {
    default: true,
    label: "Outdoor rooms",
    description: "Offer Indoor / Outdoor in Room details and the Finishes sheet (ground, boundaries, overhead).",
  },
  /**
   * Touch furniture transforms: a selected piece only moves on a phone, and
   * resize / free rotation live in the Adjust mode opened from the selection
   * bar. Direct grips only show on pieces large on screen. Off keeps the
   * always-on grips (still outside the outline). Default ON.
   * QA: `?flags=touch_adjust_mode:0`.
   */
  touch_adjust_mode: {
    default: true,
    label: "Touch Adjust mode",
    description: "On phones, resize and rotate furniture in an explicit Adjust mode so drags always move pieces.",
  },
};

/** Flag gating room finishes (the Finishes sheet and its entry points). */
export const ROOM_FINISHES_FLAG: FlagKey = "room_finishes";

/** Flag gating the touch Adjust mode for furniture. */
export const TOUCH_ADJUST_MODE_FLAG: FlagKey = "touch_adjust_mode";

/** Flag gating outdoor rooms (phase 2 of room finishes). */
export const OUTDOOR_ROOMS_FLAG: FlagKey = "outdoor_rooms";

/** Flag gating the collaboration first-run flow. */
export const COLLAB_ONBOARDING_FLAG: FlagKey = "collab_onboarding";

/** Flag gating the feng shui first-run flow. */
export const FENG_SHUI_ONBOARDING_FLAG: FlagKey = "feng_shui_onboarding";

/** Flag gating the 3D Render button. */
export const RENDER_3D_FLAG: FlagKey = "render_3d";

/** Flag gating the 3D walk-through first-run flow. */
export const RENDER_3D_ONBOARDING_FLAG: FlagKey = "render_3d_onboarding";

export type FlagKey = string;

/** Read the current flag value synchronously (outside of React). */
export function getFlag(key: FlagKey): boolean {
  return getResolvedFlags()[key] ?? FLAGS[key]?.default ?? false;
}

/** Subscribe to a single flag from a React component. */
export function useFlag(key: FlagKey): boolean {
  return useSyncExternalStore(
    subscribeFlags,
    () => getResolvedFlags()[key] ?? FLAGS[key]?.default ?? false,
    () => FLAGS[key]?.default ?? false
  );
}

/**
 * Read all resolved flags (useful for the dev UI). Returns a stable reference
 * across renders when nothing changed — required by useSyncExternalStore.
 */
export function useAllFlags(): Partial<Record<FlagKey, boolean>> {
  return useSyncExternalStore(
    subscribeFlags,
    getResolvedFlagsSnapshot,
    getResolvedFlagsSnapshot
  );
}

/** Set a local override for a flag (persists to localStorage). */
export function setFlagOverride(key: FlagKey, value: boolean | null): void {
  setFlagOverrideInternal(key, value);
}

export function clearFlagOverride(key: FlagKey): void {
  clearFlagOverrideInternal(key);
}

export function getFlagOverrides(): Partial<Record<FlagKey, boolean>> {
  return getFlagOverridesInternal();
}
